import { Ref, computed, ref } from "vue"

/**
 * Representational state machine of stateful data.
 */
export function useStateFlow() {
  const result = ref<State<any>>(new InitialResult())

  function setLoadingResult() {
    result.value = new LoadingResult()
  }

  function setFailureResult(ex: Error, logError = true) {
    if (logError) console.error(ex)
    const message = ex.message ?? "An unexpected error occurred, please try again!"
    result.value = new FailureResult({ ex, message })
  }

  function setSuccessResult<T>(data: Nullable<T>) {
    result.value = new SuccessResult<T>(data)
  }

  return {
    result,
    setLoadingResult,
    setFailureResult,
    setSuccessResult
  }
}

/**
 * Expressions of joined state machine stateful data.
 * @param args any states to be in the expression
 */
export function useStateExpression(...args: Ref<State<any>>[]) {
  function isLoading(): boolean {
    for (let i = 0; i < args.length; i++) {
      const state: State<any> = args[i].value
      if (state.isLoading()) return true
    }
    return false
  }

  function isFailure(): boolean {
    for (let i = 0; i < args.length; i++) {
      const state: State<any> = args[i].value
      if (state.isFailure()) return true
    }
    return false
  }

  function isSuccess(): boolean {
    for (let i = 0; i < args.length; i++) {
      const state: State<any> = args[i].value
      if (state.isSuccess()) return true
    }
    return false
  }

  const errorMessage = computed<Nullable<string>>(() => {
    for (let i = 0; i < args.length; i++) {
      const state: State<any> = args[i].value
      if (state.isFailure()) return state.getErrorMessage()
    }
    return null
  })

  return {
    isLoading,
    isFailure,
    isSuccess,
    errorMessage
  }
}

/**
 * Type definition for error with a localized message.
 */
export type ErrorWithMessage = {
  ex: Error
  message: string
}

/**
 * TypeGuard for possible state values
 *
 */
interface FailureFunction {
  (): this is FailureResult
}
interface SuccessFunction<T> {
  (): this is SuccessResult<T>
}
interface LoadingFunction {
  (): this is LoadingResult
}

/**
 * Representational state of a flow of stateful data.
 */
export interface State<T> {
  readonly data: Nullable<T>
  readonly error: Nullable<ErrorWithMessage>
  readonly isLoading: LoadingFunction
  readonly isFailure: FailureFunction
  readonly isSuccess: SuccessFunction<T>
  readonly getData: () => Nullable<any>
  readonly getError: () => Nullable<Error>
  readonly getErrorMessage: () => Nullable<string>
}

/**
 * Specialized {@link State} for initial state.
 */
class InitialResult implements State<any> {
  readonly data: Nullable<any> = null
  readonly error: Nullable<ErrorWithMessage> = null

  getData(): Nullable<any> {
    return null
  }

  getError(): Nullable<Error> {
    return null
  }

  getErrorMessage(): Nullable<string> {
    return null
  }

  isFailure(): this is FailureResult {
    return false
  }

  isLoading(): this is LoadingResult {
    return false
  }

  isSuccess(): this is SuccessResult<any> {
    return false
  }
}

/**
 * Specialized {@link State} for loading state.
 */
class LoadingResult implements State<any> {
  readonly data: Nullable<any> = null
  readonly error: Nullable<ErrorWithMessage> = null

  getData(): Nullable<any> {
    return null
  }

  getError(): Nullable<Error> {
    return null
  }

  getErrorMessage(): Nullable<string> {
    return null
  }

  isFailure(): this is FailureResult {
    return false
  }

  isLoading(): this is LoadingResult {
    return true
  }

  isSuccess(): this is SuccessResult<any> {
    return false
  }
}

/**
 * Specialized {@link State} for failure state.
 */
class FailureResult implements State<any> {
  constructor(value: ErrorWithMessage) {
    this.error = value
  }

  readonly data: Nullable<any> = null
  readonly error: ErrorWithMessage

  getData(): Nullable<any> {
    return null
  }

  getError(): Nullable<Error> {
    return this.error.ex
  }

  getErrorMessage(): Nullable<string> {
    return this.error.message
  }

  isFailure(): this is FailureResult {
    return true
  }

  isLoading(): this is LoadingResult {
    return false
  }

  isSuccess(): this is SuccessResult<any> {
    return false
  }
}

/**
 * Specialized {@link State} for success state.
 */
class SuccessResult<T> implements State<T> {
  constructor(value: Nullable<T>) {
    this.data = value
  }

  readonly data: Nullable<T>
  readonly error: Nullable<ErrorWithMessage> = null

  getData(): Nullable<T> {
    return this.data
  }

  getError(): Nullable<Error> {
    return null
  }

  getErrorMessage(): Nullable<string> {
    return null
  }

  isFailure(): this is FailureResult {
    return false
  }

  isLoading(): this is LoadingResult {
    return false
  }

  isSuccess(): this is SuccessResult<T> {
    return true
  }
}
